import { CdkDragEnd, DragDropModule } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { selectTenantHasFeatureFlag, selectUserHasEitherPermissions } from '@core/stores/auth/auth.selectors';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { FeatureFlags } from '@domain/tenant/tenant.enums';

import { EdgeResizeDirective, EdgeResizeEvent, EdgeResizeSize } from '@shared/directives/edge-resize';
import { UpPipe } from '@shared/pipes/up/up.pipe';

import { AiAssistantService } from './ai-assistant.service';
import { AiAssistantFullscreenComponent } from './components/ai-assistant-fullscreen/ai-assistant-fullscreen.component';
import { AiAssistantWindowComponent } from './components/ai-assistant-window/ai-assistant-window.component';

const DEFAULT_WINDOW_SIZE: EdgeResizeSize = { width: 420, height: 600 };

@Component({
  selector: 'app-ai-assistant-panel',
  imports: [DragDropModule, EdgeResizeDirective, AiAssistantWindowComponent, AiAssistantFullscreenComponent, UpPipe],
  templateUrl: './ai-assistant-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col' },
  providers: [AiAssistantService],
})
export class AiAssistantPanelComponent {
  readonly #store = inject(Store);

  public readonly aiService = inject(AiAssistantService);
  public readonly dragPosition = signal({ x: 0, y: 0 });
  public readonly windowSize = signal<EdgeResizeSize | null>(null);

  public readonly hasAiPermission = this.#store.selectSignal(
    selectUserHasEitherPermissions([
      PermissionStoreModelEnum.ViewAnySiteWithAiAssistant,
      PermissionStoreModelEnum.ViewOwnSiteWithAiAssistant,
    ])
  );
  public readonly hasAiAssistantEnabled = this.#store.selectSignal(selectTenantHasFeatureFlag(FeatureFlags.AiAssistant));

  constructor() {
    effect(() => {
      if (this.aiService.panelSize() === 'closed') {
        this.dragPosition.set({ x: 0, y: 0 });
        this.windowSize.set(null);
      }
    });
  }

  public onDragEnded(event: CdkDragEnd): void {
    this.dragPosition.set(event.source.getFreeDragPosition());
  }

  public onWindowSizeChange(event: EdgeResizeEvent): void {
    const previousSize = this.windowSize() ?? DEFAULT_WINDOW_SIZE;
    const widthDelta = event.width - previousSize.width;
    const heightDelta = event.height - previousSize.height;

    this.dragPosition.update((position) => ({
      x: position.x + (event.direction.includes('e') ? widthDelta : 0),
      y: position.y + (event.direction.includes('s') ? heightDelta : 0),
    }));
    this.windowSize.set({ width: event.width, height: event.height });
  }
}
